import React, { useRef, useState } from "react";
import { UploadCloud, X, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { uploadFileChunked, humanSize, MAX_UPLOAD_BYTES } from "../../../services/upload";
import { cls } from "../../ui";

/** Drag & drop / click-to-pick multi-file uploader with per-file chunked progress. onUploaded(uploadRecord, file) per finished file. */
export function UploadDropzone({ purpose = "MEDIA", caseId, accept, multiple = true, onUploaded, hint, compact = false, testId = "upload-dropzone", disabled }) {
  const input = useRef(null);
  const [over, setOver] = useState(false);
  const [jobs, setJobs] = useState([]);

  const run = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    for (const file of list) {
      if (file.size > MAX_UPLOAD_BYTES) { toast.error(`${file.name}: over the ${humanSize(MAX_UPLOAD_BYTES)} limit`); continue; }
      const key = `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const ctl = new AbortController();
      setJobs((j) => [...j, { key, name: file.name, size: file.size, pct: 0, status: "uploading", ctl }]);
      try {
        const rec = await uploadFileChunked(file, { purpose, caseId, signal: ctl.signal, onProgress: (p) => setJobs((j) => j.map((x) => (x.key === key ? { ...x, pct: p } : x))) });
        setJobs((j) => j.map((x) => (x.key === key ? { ...x, pct: 1, status: "done" } : x)));
        await onUploaded?.(rec, file);
        setTimeout(() => setJobs((j) => j.filter((x) => x.key !== key)), 2500);
      } catch (e) {
        const msg = ctl.signal.aborted ? "Cancelled" : e.response?.data?.detail || "Upload failed";
        setJobs((j) => j.map((x) => (x.key === key ? { ...x, status: "error", error: msg } : x)));
        if (!ctl.signal.aborted) toast.error(`${file.name}: ${msg}`);
      }
    }
  };

  return (
    <div data-testid={testId}>
      <div role="button" tabIndex={0} aria-disabled={disabled}
        onClick={() => !disabled && input.current?.click()} onKeyDown={(e) => e.key === "Enter" && !disabled && input.current?.click()}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); if (!disabled) run(e.dataTransfer.files); }}
        className={cls("border border-dashed rounded-sm flex items-center gap-2 cursor-pointer select-none transition-colors", compact ? "px-2 py-1.5" : "px-3 py-3",
          over ? "border-ujms bg-ujms-soft" : "border-slate-300 bg-[#fafbfc] hover:bg-slate-50", disabled && "opacity-50 cursor-not-allowed")} data-testid={`${testId}-area`}>
        <UploadCloud className={cls("shrink-0 text-ujms-dark", compact ? "w-4 h-4" : "w-5 h-5")} />
        <div className="min-w-0">
          <div className="text-[12px] font-semibold text-slate-800">{over ? "Release to upload" : multiple ? "Drop files here or click to choose" : "Drop a file here or click to choose"}</div>
          <div className="text-[10.5px] text-slate-500">{hint || `Photos, video, audio, PDF and documents · up to ${humanSize(MAX_UPLOAD_BYTES)} each · stored in encrypted court object storage`}</div>
        </div>
        <input ref={input} type="file" className="hidden" accept={accept} multiple={multiple} onChange={(e) => { run(e.target.files); e.target.value = ""; }} data-testid={`${testId}-input`} />
      </div>
      {jobs.length > 0 && (
        <ul className="mt-1 space-y-1" data-testid={`${testId}-jobs`}>
          {jobs.map((j) => (
            <li key={j.key} className="border border-slate-200 bg-white rounded-sm px-2 py-1 text-[11px]" data-testid={`${testId}-job`} data-status={j.status}>
              <div className="flex items-center gap-2">
                {j.status === "done" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : j.status === "error" ? <AlertCircle className="w-3.5 h-3.5 text-red-600" /> : <span className="w-3.5 h-3.5 rounded-full border-2 border-ujms border-t-transparent animate-spin" />}
                <span className="truncate flex-1 font-medium text-slate-800">{j.name}</span>
                <span className="text-slate-500">{humanSize(j.size)}</span>
                <span className="w-9 text-right font-mono">{j.status === "error" ? "—" : `${Math.round(j.pct * 100)}%`}</span>
                {j.status === "uploading" && <button className="text-slate-400 hover:text-red-700" onClick={(e) => { e.stopPropagation(); j.ctl.abort(); }} title="Cancel" data-testid={`${testId}-cancel`}><X className="w-3 h-3" /></button>}
              </div>
              <div className="h-1 bg-slate-200 rounded-sm mt-1"><div className={cls("h-1 rounded-sm transition-[width]", j.status === "error" ? "bg-red-500" : "bg-ujms-dark")} style={{ width: `${Math.round(j.pct * 100)}%` }} /></div>
              {j.error && <div className="text-red-700 mt-0.5">{j.error}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
